import { ChevronRightRounded, ReportProblemOutlined } from '@material-ui/icons';
import { List, ListItem, ListItemText } from '@material-ui/core';

import {
  CustomizationsGroups,
  LoadingCatalog,
  OutOfStock,
  ProductsByCategory,
  SearchResult,
  TurnOffCustomization,
  TurnOffCustomizationList,
  TurnOffProduct,
} from './components';

import { useMenuBody } from './hooks';

import makeClasses from './MenuBody.style';

import type { StoreId } from 'shared/domain';
import type {
  FullMenuCatalog,
  MenuOutOfStock,
} from 'features/menu-page/domain';

export function MenuBody(props: Props) {
  const {
    storeId,
    loading,
    catalog,
    outOfStock,
    searchQuery,
    refreshCatalog,
  } = props;

  const {
    lang,
    showOutOfStockBtn,
    showSearchResultsBtn,
    isNavOutOfStock,
    isNavProducts,
    isNavCustomGroups,
    isNavSearchResults,
    productToTurnOff,
    customizationToTurnOff,
    customizationToTurnOffList,
    handleShowOutOfStock,
    handleShowProducts,
    handleShowCustomGroups,
    handleShowSearchResults,
    toggleProduct,
    toggleCustomization,
    turnOffProductCTA,
    turnOffCustomizationCTA,
    turnOffCustomizationListCTA,
    cancelTurnOffCTA,
  } = useMenuBody({
    storeId,
    loading,
    searchQuery,
    catalog,
    outOfStock,
    refreshCatalog,
  });

  const _classes = makeClasses();

  return (
    <section className={_classes.board}>
      <List
        component='nav'
        aria-label='menu-navigation'
        classes={{ root: _classes.menu }}
      >
        {showOutOfStockBtn && (
          <ListItem
            button
            component='button'
            selected={isNavOutOfStock}
            onClick={handleShowOutOfStock}
            classes={{ root: _classes.outStock }}
          >
            <ReportProblemOutlined fontSize='default' />
            <ListItemText disableTypography>
              <p
                dangerouslySetInnerHTML={{ __html: lang.nav.outOfStock.btn }}
                className={_classes.outStockItemText}
              />
            </ListItemText>
            <ChevronRightRounded />
          </ListItem>
        )}

        <ListItem
          button
          component='button'
          selected={isNavProducts}
          onClick={handleShowProducts}
          classes={{ root: _classes.navOption }}
        >
          <ListItemText
            primary={lang.nav.products.btn}
            classes={{ primary: _classes.listItemText }}
          />
          <ChevronRightRounded />
        </ListItem>

        <ListItem
          button
          component='button'
          selected={isNavCustomGroups}
          onClick={handleShowCustomGroups}
          classes={{ root: _classes.navOption }}
        >
          <ListItemText
            primary={lang.nav.customGroups.btn}
            classes={{ primary: _classes.listItemText }}
          />
          <ChevronRightRounded />
        </ListItem>

        {showSearchResultsBtn && (
          <ListItem
            button
            component='button'
            selected={isNavSearchResults}
            onClick={handleShowSearchResults}
            classes={{ root: _classes.navOption }}
          >
            <ListItemText
              primary={lang.nav.searchResults.btn}
              classes={{ primary: _classes.listItemText }}
            />
            <ChevronRightRounded />
          </ListItem>
        )}
      </List>

      <main className={_classes.listingSection}>
        {loading && <LoadingCatalog />}
        {isNavOutOfStock && (
          <OutOfStock
            catalog={catalog}
            outOfStock={outOfStock}
            toggleOnOffProduct={toggleProduct}
            toggleOnOffCustomization={toggleCustomization}
          />
        )}
        {isNavProducts && (
          <ProductsByCategory
            catalog={catalog}
            toggleOnOff={toggleProduct}
            toggleOnOffCustomization={toggleCustomization}
          />
        )}
        {isNavCustomGroups && (
          <CustomizationsGroups
            catalog={catalog}
            toggleOnOff={toggleCustomization}
          />
        )}
        {isNavSearchResults && (
          <SearchResult
            catalog={catalog}
            searchQuery={searchQuery}
            toggleOnOffProduct={toggleProduct}
            toggleOnOffCustomization={toggleCustomization}
          />
        )}
      </main>

      {/* Left Drawers */}
      <TurnOffProduct
        product={productToTurnOff}
        turnOffCTA={turnOffProductCTA}
        cancelCTA={cancelTurnOffCTA}
      />
      <TurnOffCustomization
        customization={customizationToTurnOff}
        turnOffCTA={turnOffCustomizationCTA}
        cancelCTA={cancelTurnOffCTA}
      />
      <TurnOffCustomizationList
        catalog={catalog}
        customizationList={customizationToTurnOffList.list}
        turnOffCTA={turnOffCustomizationListCTA}
        cancelCTA={cancelTurnOffCTA}
      />
    </section>
  );
}

interface Props {
  loading: boolean;
  storeId: StoreId;
  searchQuery: string;
  catalog: FullMenuCatalog;
  outOfStock: MenuOutOfStock;
  refreshCatalog: () => void;
}
